// Segment colours carried by the WHOLE document (Pavel 2026-08-06: "segmenty,
// když bysme rozdělili na nějaký, s nějakým jako barevným podložením a pak
// s těma barvama pracovali i dál, tak by to bylo skvělý").
//
// Three properties decide the design:
//
// 1. A colour is a TOKEN, never a value. `<seg color="amber">` picks one of six
//    tinted pairs whose contrast was chosen once, in the stylesheet; an author
//    cannot paint dark text on a dark tint by writing a hex, and the light-mode
//    and print overrides have a finite set to answer for. An unknown token is
//    treated as no choice at all rather than rendering an unstyled chip.
// 2. Declaration order is not reading order. Declarations are collected in a
//    pass over the parsed tree BEFORE anything renders, so `<term seg="S1">`
//    resolves whether the legend sits above it or below it. A name that is only
//    REFERENCED still gets a token, so a colour never silently disappears
//    halfway down a document because the legend forgot a row.
// 3. Auto-assignment never collides with an explicit choice: the tokens an
//    author named are reserved first, and the rest are handed out from what is
//    left. Two segments share a colour only when a document declares more than
//    six of them.

import type { DocNode } from "./doc-parse";

/** Six tints, in the order an author gets them when they do not choose. */
export const SEGMENT_TOKENS = ["amber", "blue", "cyan", "violet", "green", "rose"] as const;
export type SegmentToken = (typeof SEGMENT_TOKENS)[number];

export interface SegmentDef {
  /** The name authors reference: `<term seg="S1">`. */
  name: string;
  /** What the reader sees on the chip ("Seznam"). Defaults to the name. */
  label: string;
  /** The explanation a tooltip shows. Empty means the chip carries no tip. */
  tip: string;
  token: SegmentToken;
  /** False for a name that was referenced but never declared. */
  declared: boolean;
}

export type SegmentMap = ReadonlyMap<string, SegmentDef>;

/** `S1` and ` s1 ` are the same segment: authors retype these by hand. */
export function segmentKey(raw: string | undefined): string {
  return (raw ?? "").trim().toLowerCase();
}

function explicitToken(raw: string | undefined): SegmentToken | null {
  const value = (raw ?? "").trim().toLowerCase();
  return (SEGMENT_TOKENS as readonly string[]).includes(value) ? (value as SegmentToken) : null;
}

/** The tint each token paints, for snapping an authored hex to its token. */
const TOKEN_RGB: Record<SegmentToken, [number, number, number]> = {
  amber: [255, 175, 40],
  blue: [74, 144, 255],
  cyan: [34, 211, 238],
  violet: [167, 139, 250],
  green: [52, 211, 153],
  rose: [251, 113, 133]
};

/**
 * The e-mail blueprint declares segment colours as hex
 * (`<seg color="#5b9bff">`). A hex never reaches a style attribute - the
 * token contract holds - but ignoring it entirely handed the first segment
 * AMBER, the one hue the document reserves for structure. Snapping to the
 * nearest token keeps the author's intent inside the closed, contrast-safe
 * set; anything that is not a colour still counts as no choice at all.
 */
function hexToken(raw: string | undefined): SegmentToken | null {
  const match = /^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.exec((raw ?? "").trim());
  if (!match) return null;
  const hex = match[1].length === 3 ? [...match[1]].map((c) => c + c).join("") : match[1];
  const channel = (at: number) => parseInt(hex.slice(at, at + 2), 16);
  const [r, g, b] = [channel(0), channel(2), channel(4)];
  let best: SegmentToken = SEGMENT_TOKENS[0];
  let bestDistance = Infinity;
  for (const token of SEGMENT_TOKENS) {
    const [tr, tg, tb] = TOKEN_RGB[token];
    const distance = (r - tr) ** 2 + (g - tg) ** 2 + (b - tb) ** 2;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = token;
    }
  }
  return best;
}

interface RawSegment {
  key: string;
  name: string;
  label: string;
  tip: string;
  color: SegmentToken | null;
  /** Token snapped from an authored hex; yields to explicit names. */
  snapped: SegmentToken | null;
}

export function collectSegments(nodes: DocNode[]): SegmentMap {
  const declared: RawSegment[] = [];
  const referenced: string[] = [];
  const seenDeclaration = new Set<string>();

  const visit = (node: DocNode): void => {
    if (node.kind !== "el") return;
    if (node.tag === "seg" && node.attrs.name) {
      const key = segmentKey(node.attrs.name);
      if (key && !seenDeclaration.has(key)) {
        seenDeclaration.add(key);
        const named = explicitToken(node.attrs.color);
        declared.push({
          key,
          name: node.attrs.name.trim(),
          label: (node.attrs.label ?? node.attrs.name).trim(),
          tip: (node.attrs.tip ?? "").trim(),
          color: named,
          snapped: named ? null : hexToken(node.attrs.color)
        });
      }
    }
    const ref = segmentKey(node.attrs.seg);
    if (ref) referenced.push(ref);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);

  const taken = new Set<SegmentToken>();
  for (const segment of declared) if (segment.color) taken.add(segment.color);
  // A snapped hex claims its token only when nobody holds it yet: an explicit
  // name always wins, and two hexes landing on one token would make two
  // segments read as one, so the second falls back to auto-assignment.
  for (const segment of declared) {
    if (segment.color || !segment.snapped || taken.has(segment.snapped)) continue;
    segment.color = segment.snapped;
    taken.add(segment.snapped);
  }
  let cursor = 0;
  const nextFreeToken = (): SegmentToken => {
    for (let step = 0; step < SEGMENT_TOKENS.length; step += 1) {
      const candidate = SEGMENT_TOKENS[(cursor + step) % SEGMENT_TOKENS.length];
      if (!taken.has(candidate)) {
        cursor = (cursor + step + 1) % SEGMENT_TOKENS.length;
        taken.add(candidate);
        return candidate;
      }
    }
    // More segments than tokens: reuse, in order, rather than leave one blank.
    const fallback = SEGMENT_TOKENS[cursor % SEGMENT_TOKENS.length];
    cursor += 1;
    return fallback;
  };

  const map = new Map<string, SegmentDef>();
  for (const segment of declared) {
    map.set(segment.key, {
      name: segment.name,
      label: segment.label,
      tip: segment.tip,
      token: segment.color ?? nextFreeToken(),
      declared: true
    });
  }
  for (const key of referenced) {
    if (map.has(key)) continue;
    map.set(key, { name: key, label: key, tip: "", token: nextFreeToken(), declared: false });
  }
  return map;
}

/** The classes a coloured element carries, or undefined when it has no segment. */
export function segmentClass(segments: SegmentMap, raw: string | undefined): string | undefined {
  const def = segments.get(segmentKey(raw));
  return def ? `doc-seg doc-seg-${def.token}` : undefined;
}

export function segmentDef(segments: SegmentMap, raw: string | undefined): SegmentDef | undefined {
  return segments.get(segmentKey(raw));
}
